import { useCallback, useEffect, useState } from 'react';

import {
  IBudgetServiceResponse,
  fetchBudgetServicePackageItems,
  fetchBudgetServicePackages,
} from '../../../../api/fetchBudget';
import type { BudgetServiceCategory } from '../../../../models/budget';
import { ISelectedServices, IVehicleData } from '../types';
import { Select } from '@rio-cloud/rio-uikit';
import Notification from '@rio-cloud/rio-uikit/Notification';
import { ITruckerPayPlanData } from '../../../../models/truckerPayPlan';
import SearchAutocomplete from '../../../form/searchAutocomplete';

interface IBudgetServiceSelectsProps {
  serviceId: string;
  serviceName?: string;
  vehicleData?: IVehicleData;
  serviceType: string;
  truckerPayPlan?: ITruckerPayPlanData;
  onChangeItem: (itemId: string, serviceData?: IBudgetServiceResponse) => void;
  onChangeModoFalha: (modoFalhaId: string) => void;
  onChangeServiceType: (serviceType: string) => void;
  usedServices: ISelectedServices[];
  onPackageItemsLoadingChange?: (loading: boolean) => void;
}

const packageCategory = (
  type: string
): BudgetServiceCategory | null =>
  type === 'PREVENTIVE' || type === 'PLAN' ? type : null;

const showsPackageAutocomplete = (type: string): boolean =>
  type === 'PREVENTIVE' || type === 'PLAN';

const BudgetServiceSelects = (props: IBudgetServiceSelectsProps) => {
  const [serviceType, setServiceType] = useState<string>(props.serviceType);
  const [serviceId, setServiceId] = useState<string>(props.serviceId);
  const [packageSelectKey, setPackageSelectKey] = useState(0);
  const [loadingPackageItems, setLoadingPackageItems] = useState(false);

  const effectiveServiceType =
    serviceType === 'CORRECTIVE' ? 'PLAN' : serviceType;

  const loadServiceData = useCallback(
    async (
      searchTerm: string
    ): Promise<{ id: string; label: string }[]> => {
      const category = packageCategory(effectiveServiceType);
      if (!category) {
        return [];
      }
      const chassis = props.vehicleData?.chassis ?? '';
      const odometer = props.vehicleData?.odometer;
      const hourMeter = props.vehicleData?.hourmeter;
      return fetchBudgetServicePackages(
        category,
        chassis,
        searchTerm,
        0,
        20,
        odometer,
        hourMeter
      );
    },
    [
      props.vehicleData?.chassis,
      props.vehicleData?.odometer,
      props.vehicleData?.hourmeter,
      effectiveServiceType,
    ]
  );

  useEffect(() => {
    setServiceId(props.serviceId);
    setServiceType(props.serviceType || 'PREVENTIVE');
  }, [props.serviceId, props.serviceType]);

  const applyServiceSelection = (
    option:
      | { id: string; label: string; serviceData?: IBudgetServiceResponse }
      | undefined
  ) => {
    const newServiceId = option?.id || '';
    setServiceId(newServiceId);
    props.onChangeModoFalha('');

    if (newServiceId) {
      const servicesFiltered = props.usedServices.filter(
        (item) => item.id === newServiceId
      );
      if (servicesFiltered.length) {
        Notification.error(
          'Não pode existir dois pacote de serviços iguais'
        );
        return;
      }
    }

    props.onChangeItem(newServiceId, option?.serviceData);
  };

  const handlePackageSelected = async (option: {
    id: string;
    label: string;
  }) => {
    const category = packageCategory(effectiveServiceType);
    if (!category) {
      return;
    }

    if (
      props.usedServices.some((item) => item.id === option.id)
    ) {
      Notification.error('Não pode existir dois pacote de serviços iguais');
      setPackageSelectKey((k) => k + 1);
      return;
    }

    setLoadingPackageItems(true);
    props.onPackageItemsLoadingChange?.(true);
    try {
      const lines = await fetchBudgetServicePackageItems(
        category,
        option.id,
        props.vehicleData?.chassis ?? '',
        props.vehicleData?.odometer,
        props.vehicleData?.hourmeter
      );
      if (!lines.length) {
        Notification.error('Não foi possível carregar os itens do pacote.');
        setPackageSelectKey((k) => k + 1);
        return;
      }

      const serviceItems = lines.map((row) => ({
        item: row.ref,
        description: row.description,
        quantity: row.qtd,
        value: row.value,
        discount: row.discount ?? 0,
        subtotal: row.subtotal,
        isCovered: row.isCovered,
        notCoveredReason: row.notCoveredReason,
        availableIn: row.availableIn,
      }));

      const serviceData: IBudgetServiceResponse = {
        id: option.id,
        name: option.label,
        code: option.id,
        item: option.id,
        ref: option.id,
        value: null,
        serviceItems,
      };

      applyServiceSelection({
        id: option.id,
        label: option.label,
        serviceData,
      });
    } finally {
      setLoadingPackageItems(false);
      props.onPackageItemsLoadingChange?.(false);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row gap-10 flex-1 w-full">
      <div className="w-full sm:w-[210px]">
        <div className="font-bold mb-2 text-size-12 text-gray-500 whitespace-nowrap">
          Tipo de serviço
        </div>
        <Select
          options={[
            { id: 'PREVENTIVE', label: 'Preventiva' },
            ...(props.truckerPayPlan
              ? [{ id: 'PLAN', label: 'Sob Medida' }]
              : []),
            { id: 'OTHER', label: 'Demais serviços' },
          ]}
          placeholder="Tipo de serviço"
          value={[
            props.serviceType === 'PLAN' && !props.truckerPayPlan
              ? 'PREVENTIVE'
              : effectiveServiceType,
          ]}
          onChange={(option) => {
            const next = option?.id || '';
            props.onChangeServiceType(next);
            props.onChangeModoFalha('');
            setPackageSelectKey((k) => k + 1);
            applyServiceSelection(undefined);
          }}
        />
      </div>

      {showsPackageAutocomplete(serviceType) && (
        <div className="w-full sm:w-[210px]">
          <div className="font-bold mb-2 text-size-12 text-gray-500 whitespace-nowrap">
            Pacote de serviços *
          </div>
          <SearchAutocomplete
            key={`${packageSelectKey}-${effectiveServiceType}`}
            id="service-select"
            placeholder="Selecione o pacote de serviços"
            fetchOptions={loadServiceData}
            fetchDefaultOptionsOnEmptySearch
            optionLabel={{ label: 'Nome' }}
            optionRenderer={({ label }) => <div>{label}</div>}
            onSelectedOption={handlePackageSelected}
            overlay={true}
            debounceDelay={300}
            disabled={loadingPackageItems}
            defaultValue={
              props.serviceId
                ? {
                    id: props.serviceId,
                    label: props.serviceName || props.serviceId,
                  }
                : undefined
            }
            valueRenderer={(option) =>
              option?.label || 'Selecione o pacote de serviços'
            }
            onClear={() => applyServiceSelection(undefined)}
          />
        </div>
      )}
    </div>
  );
};

export default BudgetServiceSelects;
